import { z } from 'zod';
/**
 * Password policy.
 *
 * Length over composition rules. NIST 800-63B is explicit that forced
 * character-class rules push people toward predictable substitutions
 * ("P@ssw0rd1") while a longer passphrase is genuinely stronger. 12 characters
 * minimum, no composition requirement, and the API additionally rejects
 * anything on a common-password list — which catches far more real weakness
 * than a symbol requirement ever does.
 */
export const passwordSchema = z
    .string()
    .min(12, 'Password must be at least 12 characters')
    .max(200, 'Password must be at most 200 characters');
export const loginSchema = z.object({
    email: z.string().email().toLowerCase().trim(),
    password: z.string().min(1),
    /** Optional label so a user can recognise the session in the session list. */
    deviceLabel: z.string().max(80).optional(),
});
export const mfaVerifySchema = z.object({
    challengeToken: z.string().min(1),
    code: z
        .string()
        .regex(/^\d{6}$|^[A-Z0-9]{4}-[A-Z0-9]{4}$/, 'Enter a 6-digit code or a recovery code'),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(1),
});
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
})
    .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'New password must differ from the current one',
    path: ['newPassword'],
});
export const mfaConfirmSchema = z.object({
    code: z.string().regex(/^\d{6}$/),
});
export const mfaDisableSchema = z.object({
    password: z.string().min(1),
    code: z.string().regex(/^\d{6}$/),
});
export const userSummarySchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    fullName: z.string(),
    avatarUrl: z.string().nullable(),
    roles: z.array(z.string()),
    permissions: z.array(z.string()),
    scopes: z.array(z.object({ scopeType: z.enum(['REGION', 'SITE']), scopeId: z.string().uuid() })),
    mfaEnabled: z.boolean(),
    mfaRequired: z.boolean(),
    theme: z.string(),
    density: z.string(),
});
/**
 * Login result is a discriminated union rather than a single shape with
 * optional fields. A client cannot then accidentally treat an MFA challenge as
 * a successful login by reading `accessToken` and finding `undefined`.
 */
export const loginResultSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('MFA_REQUIRED'),
        challengeToken: z.string(),
        methods: z.array(z.enum(['TOTP', 'RECOVERY_CODE'])),
        expiresIn: z.number().int(),
    }),
    z.object({
        status: z.literal('AUTHENTICATED'),
        accessToken: z.string(),
        refreshToken: z.string(),
        expiresIn: z.number().int(),
        user: userSummarySchema,
    }),
]);
export const mfaEnrolResultSchema = z.object({
    secret: z.string(),
    otpauthUri: z.string(),
    qrSvg: z.string(),
});
export const sessionSchema = z.object({
    id: z.string().uuid(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    deviceLabel: z.string().nullable(),
    current: z.boolean(),
    lastSeenAt: z.coerce.date(),
    createdAt: z.coerce.date(),
    expiresAt: z.coerce.date(),
});
//# sourceMappingURL=auth.js.map